import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { disabled, form, FormField, required, validate } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AbstractDialog,
  AbstractDialogResultData,
  DIALOG_RETURN_TYPES,
  DialogComponent,
  DialogService,
} from '@next/core-lib/dialog';
import { AgrI18nSharedModule, LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';

import { SprayTypes } from '../../../models';
import { DateService } from '../../../services/date.service';
import {
  buildSprayApplicationDate,
  formatDefaultTime,
  normalizeSprayApplicationTime,
  parseSprayApplicationTime,
  sanitizeSprayApplicationTimeInput,
  SprayApplicationDialogData,
  SprayApplicationDialogResult,
} from '../../models/spray-application.models';
import { SprayApplicationSignalStore } from '../../signal-store/spray-application-signal-store';
import { FmsSyncStore } from '@app/shared/fms-sync/fms-sync.store';

interface SprayApplicationFormValue {
  date: Date;
  time: string;
  sprayType: SprayTypes;
  sprayId: number | null;
}

@Component({
  selector: 'app-spray-application-dialog',
  standalone: true,
  templateUrl: './spray-application-dialog.component.html',
  styleUrls: ['./spray-application-dialog.component.scss'],
  imports: [
    CommonModule,
    FormField,
    MatButtonModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    DialogComponent,
    AgrI18nSharedModule,
    DatePipe,
  ],
})
export class SprayApplicationDialogComponent extends AbstractDialog<SprayApplicationDialogResult> {
  readonly data = inject<SprayApplicationDialogData>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef =
    inject<MatDialogRef<SprayApplicationDialogComponent, AbstractDialogResultData<SprayApplicationDialogResult>>>(
      MatDialogRef,
    );

  protected readonly store = inject(SprayApplicationSignalStore);
  private readonly fmsSyncStore = inject(FmsSyncStore);

  private readonly dateService = inject(DateService);
  private readonly localeService = inject(LocaleService);
  private readonly dialogService = inject(DialogService);
  private readonly breakpointService = inject<BreakpointService>(BreakpointServiceInjector);

  protected readonly isOnMobile = toSignal(this.breakpointService.ltMd$, { initialValue: false });
  protected readonly groupedTreatments = this.store.groupedTreatments;

  protected readonly isFmsReadonlyMode = computed(() => this.fmsSyncStore.isEnabled());
  protected readonly isReadonlyMode = computed(() => this.isFmsReadonlyMode() || Boolean(this.data.readOnly));

  protected readonly sprayTypes = [
    { value: SprayTypes.PreventiveSpray, label: 'treatments-type-1' },
    { value: SprayTypes.CurativeSpray, label: 'treatments-type-2' },
    { value: SprayTypes.StopSpray, label: 'treatments-type-3' },
  ];

  protected readonly availableSprayTypes = computed(() =>
    this.sprayTypes.filter((sprayType) => this.store.availableSprayTypes().includes(sprayType.value)),
  );

  protected readonly showSprayTypeSelection = computed(() => this.availableSprayTypes().length > 1);
  protected readonly fmsReadonlyMessageKey = 'shared.diseaseTable.fmsManagedActionsMessage';
  protected readonly mappedCropFieldNoSpraysIcon = 'check_circle';
  protected readonly mappedCropFieldNoSpraysNotificationClass = 'notification-success';

  protected readonly sprayHistoryHeaderKey = computed(() =>
    this.isReadonlyMode() ? 'sprayApplications.spray' : 'sprayApplications.predefinedSpray',
  );

  protected readonly displayedColumns = computed(() =>
    this.isReadonlyMode()
      ? ['date', 'time', 'sprayNumber', 'spray']
      : ['date', 'time', 'sprayNumber', 'spray', 'actions'],
  );

  private readonly hasSubmitted = signal(false);
  private readonly lastSelectedSprayName = signal<string | null>(null);
  private readonly model = signal<SprayApplicationFormValue>({
    date: this.data.defaultDate ?? this.dateService.now(),
    time: formatDefaultTime(this.data.defaultDate ?? this.dateService.now()),
    sprayType: this.data.initialSprayType,
    sprayId: null,
  });

  protected readonly sprayApplicationForm = form(this.model, (formPath) => {
    disabled(formPath.date, () => this.isReadonlyMode());
    disabled(formPath.time, () => this.isReadonlyMode());
    disabled(formPath.sprayType, () => this.isReadonlyMode());
    disabled(formPath.sprayId, () => this.isReadonlyMode());

    required(formPath.date);
    required(formPath.time);
    required(formPath.sprayType);
    required(formPath.sprayId);
    validate(formPath.time, ({ value }) => {
      const time = value().trim();
      if (!time || parseSprayApplicationTime(time)) {
        return;
      }

      return {
        kind: 'invalidTimeFormat',
      };
    });
  });

  protected readonly createSucceeded = computed(
    () =>
      this.hasSubmitted() &&
      !this.store.createSprayApplicationIsPending() &&
      !this.store.createSprayApplicationError() &&
      this.store.createSprayApplicationStatus() === 'success',
  );
  protected readonly selectedCropFields = this.store.selectedCropFields;
  protected readonly groupedHistoryByCropFieldId = computed(() =>
    new Map(this.groupedHistory().filter((g) => g.sprays.length > 0).map((g) => [g.cropFieldId, g])),
  );
  protected readonly sprayCocktails = this.store.sprayCocktailOptions;
  protected readonly groupedHistory = this.store.groupedSprayHistory;
  protected readonly isSubmitting = this.store.createSprayApplicationIsPending;
  protected readonly isLoadingHistory = this.store.sprayApplicationHistoryIsLoading;
  protected readonly isLoadingSprays = this.store.sprayCocktailsIsLoading;
  protected readonly isTimeEntryValid = computed(
    () => parseSprayApplicationTime(this.sprayApplicationForm.time().value()) !== null,
  );
  protected readonly showTimeFormatWarning = computed(() => {
    const timeFieldErrors = this.sprayApplicationForm.time().errors();
    return timeFieldErrors.some((error) => error.kind === 'invalidTimeFormat');
  });
  protected readonly selectedSpray = computed(
    () =>
      this.sprayCocktails().find((sprayCocktail) => sprayCocktail.id === this.sprayApplicationForm.sprayId().value()) ??
      null,
  );
  protected readonly selectedSprayDisplayName = computed(() => {
    const selectedSpray = this.selectedSpray();
    if (selectedSpray) {
      return selectedSpray.name;
    }

    if (this.sprayApplicationForm.sprayId().value() !== null) {
      return this.lastSelectedSprayName();
    }

    return null;
  });
  protected readonly isAddDisabled = computed(
    () =>
      this.isReadonlyMode() ||
      this.sprayApplicationForm().invalid() ||
      !this.store.hasSelectedCropFields() ||
      this.isSubmitting(),
  );

  protected get locale(): string {
    return this.localeService.getLocale();
  }

  override cancel(result?: SprayApplicationDialogResult): void {
    super.cancel(result ?? { modifiedCropFieldIds: this.store.modifiedCropFieldIds() });
  }

  constructor() {
    super();

    this.data.confirmButton = false;

    effect(() => {
      const time = this.sprayApplicationForm.time().value();
      if (!parseSprayApplicationTime(time)) {
        return;
      }

      this.store.setApplicationDate(
        buildSprayApplicationDate(this.sprayApplicationForm.date().value(), normalizeSprayApplicationTime(time)),
      );
    });

    effect(() => {
      const availableSprayTypes = this.store.availableSprayTypes();
      if (!availableSprayTypes.length) {
        return;
      }

      const selectedSprayType = this.sprayApplicationForm.sprayType().value();
      if (!availableSprayTypes.includes(selectedSprayType)) {
        this.sprayApplicationForm.sprayType().value.set(availableSprayTypes[0]);
      }
    });

    effect(() => {
      this.store.setSprayType(this.sprayApplicationForm.sprayType().value());
      this.sprayApplicationForm.sprayId().value.set(null);
      this.lastSelectedSprayName.set(null);
    });

    effect(() => {
      const selectedSpray = this.selectedSpray();
      const selectedSprayId = this.sprayApplicationForm.sprayId().value();

      if (selectedSpray) {
        this.lastSelectedSprayName.set(selectedSpray.name);
      } else if (selectedSprayId === null) {
        this.lastSelectedSprayName.set(null);
      }
    });

    effect(() => {
      if (this.createSucceeded()) {
        this.sprayApplicationForm.sprayId().value.set(null);
        this.lastSelectedSprayName.set(null);
        this.hasSubmitted.set(false);
      }
    });
  }

  protected removeCropField(cropFieldId: number): void {
    if (this.isReadonlyMode()) {
      return;
    }

    this.store.removeCropField(cropFieldId);
  }

  protected submitSprayApplication(): void {
    if (this.isReadonlyMode()) {
      return;
    }

    const sprayId = this.sprayApplicationForm.sprayId().value();
    if (!sprayId || this.isAddDisabled()) {
      return;
    }

    this.sprayApplicationForm.time().value.set(normalizeSprayApplicationTime(this.sprayApplicationForm.time().value()));
    this.hasSubmitted.set(true);
    this.store.createSprayRegistration(sprayId);
  }

  protected updateTimeInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }

    const sanitizedTime = sanitizeSprayApplicationTimeInput(input.value);
    input.value = sanitizedTime;
    this.sprayApplicationForm.time().value.set(sanitizedTime);
  }

  protected normalizeTimeInput(): void {
    this.sprayApplicationForm.time().value.set(normalizeSprayApplicationTime(this.sprayApplicationForm.time().value()));
  }

  protected deleteSprayApplication(cropFieldId: number, sprayApplicationId: string): void {
    if (this.isReadonlyMode()) {
      return;
    }

    this.dialogService
      .confirm({
        title: this.localeService.translate('sprayApplications.deleteConfirmMessage'),
        pushHistoryState: false,
      })
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.deleteSprayRegistration(cropFieldId, sprayApplicationId);
        }
      });
  }
}
